import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Subject } from 'rxjs';

import {
  FormField,
  TableSection,
} from '@app/shared/components/add-form/add-form.component';
import {
  TableColumn,
  DataTableComponent,
} from '@app/shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { WarehouseWarrantInfoVO } from '@app/api/model/warehouseWarrantInfoVO';
import { WarehouseWarrantDetailItem } from '../models/warehouse-warrant-detail-item';
import { INBOUND_NOTICE_STATUS_OPTIONS, InboundType } from '../inbound.config';
import { formatDateTimeUserFriendly } from '@app/shared/utils/date-formatter.util';
import { getInboundStatusSeverity, getInboundStatusStyle, getInboundTypeLabel, getInboundStatusLabel } from '../inbound.util';

@Component({
  selector: 'app-inbound-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  templateUrl: './inbound-form-wrapper.component.html',
  styleUrl: './inbound-form-wrapper.component.css',
})
export class InboundFormWrapperComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() set inboundData(value: WarehouseWarrantInfoVO | null) {
    this._inboundData = value;
    this.updateFormFields();
    this.updateStatusForm();
    this.forceRerender();
  }
  get inboundData(): WarehouseWarrantInfoVO | null {
    return this._inboundData;
  }
  private _inboundData: WarehouseWarrantInfoVO | null = null;

  @Input() set itemDetails(value: WarehouseWarrantDetailItem[]) {
    this._itemDetails = value;
    this.updateTableSections();
  }
  get itemDetails(): WarehouseWarrantDetailItem[] {
    return this._itemDetails;
  }
  private _itemDetails: WarehouseWarrantDetailItem[] = [];

  @Input() viewMode = false;
  @Input() editMode = false;
  @Input() addMode = false;
  @Input() loading = false;
  @Input() saving = false;
  @Input() statusChangeConfirmed = false;
  @Input() statusAutoCorrected = false;
  @Input() set correctedStatus(value: string | null) {
    if (value && this.statusForm) {
      this.statusForm.get('status')?.setValue(value);
      this.cdr.detectChanges();
    }
  }
  get correctedStatus(): string | null {
    return this._correctedStatus;
  }
  private readonly _correctedStatus: string | null = null;
  @Input() set tabType(
    value: 'inbound-notice' | 'inbound-warrant' | 'inbound-result'
  ) {
    this._tabType = value;
    this.updateTableColumns();
    this.updateTableSections();
  }
  get tabType(): 'inbound-notice' | 'inbound-warrant' | 'inbound-result' {
    return this._tabType;
  }
  private _tabType: 'inbound-notice' | 'inbound-warrant' | 'inbound-result' =
    'inbound-warrant';

  @Output() formAction = new EventEmitter<{ action: string; data?: any }>();
  @Output() tableAction = new EventEmitter<{
    section: string;
    action: string;
    data?: any;
  }>();
  @Output() statusChange = new EventEmitter<{
    status: string;
    action?: string;
  }>();

  // Status selection for edit mode
  statusForm!: FormGroup;
  statusOptions: any[] = [];
  selectedStatus: string = '';

  // Dynamic status actions for buttons
  availableStatusActions: any[] = [];

  // Form fields configuration
  formFields: FormField[] = [];
  tableSections: TableSection[] = [];

  // Table columns for Item Details (dynamically generated)
  itemTableColumns: TableColumn[] = [];

  ngOnInit(): void {
    this.initializeFormFields();
    this.initializeTableSections();
    this.initializeStatusForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormFields(): void {
    this.formFields = [
      // Basic Information Section
      {
        label: 'inbound.fields.inbound_type',
        controlName: 'inputType',
        type: 'text' as const,
        section: 'inbound.sections.basic_information',
        required: true,
        readonly: true,
      },
      // Hide status field for inbound-result tab
      ...(this.tabType !== 'inbound-result'
        ? [
            {
              label: 'inbound.fields.inbound_status',
              controlName: 'status',
              type: 'text' as const,
              section: 'inbound.sections.basic_information',
              required: true,
              readonly: true,
            },
          ]
        : []),
      // Conditionally include Inbound Result Code for inbound-result tab, otherwise hide Inbound Warrant ID
      ...(this.tabType === 'inbound-result'
        ? [
            {
              label: 'inbound.fields.inbound_result_code',
              controlName: 'code',
              type: 'text' as const,
              section: 'inbound.sections.basic_information',
              required: true,
              readonly: true,
            },
          ]
        : []),
      {
        label: 'inbound.fields.inbound_warrant_code',
        controlName:
          this.tabType === 'inbound-result' ? 'warehouseWarrantCode' : 'code',
        type: 'text' as const,
        section: 'inbound.sections.basic_information',
        required: true,
        readonly: true,
      },
      {
        label: 'inbound.fields.internal_order_number',
        controlName: 'orderCode',
        type: 'text' as const,
        section: 'inbound.sections.basic_information',
        readonly: true,
      },
      {
        label: 'inbound.fields.third_party_order_number',
        controlName: 'platformOrderCode',
        type: 'text' as const,
        section: 'inbound.sections.basic_information',
        readonly: true,
      },
      {
        label: 'inbound.fields.preceding_document_number',
        controlName: 'frontRecordCode',
        type: 'text' as const,
        section: 'inbound.sections.basic_information',
        readonly: true,
      },
      {
        label: 'inbound.fields.required_inbound_quantity',
        controlName: 'totalQty',
        type: 'number' as const,
        section: 'inbound.sections.basic_information',
        readonly: true,
      },
      // Add Received Quantity field for inbound-result tab
      ...(this.tabType === 'inbound-result'
        ? [
            {
              label: 'inbound.fields.received_quantity',
              controlName: 'alreadyTotalQty',
              type: 'number' as const,
              section: 'inbound.sections.basic_information',
              readonly: true,
            },
          ]
        : []),
      {
        label: 'inbound.fields.creation_time',
        controlName: 'creationDate',
        type: 'text' as const,
        section: 'inbound.sections.basic_information',
        readonly: true,
      },

      // Warehouse Information Section
      {
        label: 'inbound.fields.receiving_logical_warehouse_code',
        controlName: 'receivingLogicWarehouseCode',
        type: 'text' as const,
        section: 'inbound.sections.warehouse_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.receiving_logical_warehouse_name',
        controlName: 'receivingLogicWarehouseName',
        type: 'text' as const,
        section: 'inbound.sections.warehouse_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      // Store Information Section (for Transfer Orders)
      {
        label: 'inbound.fields.store_name',
        controlName: 'storeName',
        type: 'text' as const,
        section: 'inbound.sections.store_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.channel_type_code',
        controlName: 'channelTypeCode',
        type: 'text' as const,
        section: 'inbound.sections.store_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },

      // Logistics Information Section (will be added after Item Details)
      {
        label: 'inbound.fields.logistics_company_name',
        controlName: 'logisticsCompanyName',
        type: 'text' as const,
        section: 'inbound.sections.logistics_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.logistics_number',
        controlName: 'logisticsNumber',
        type: 'text' as const,
        section: 'inbound.sections.logistics_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },

      // Customer Information Section (for Purchase Orders)
      {
        label: 'inbound.fields.buyer_nickname',
        controlName: 'sorBuyerNikeName',
        type: 'text' as const,
        section: 'inbound.sections.customer_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.order_message',
        controlName: 'orderMsg',
        type: 'text' as const,
        section: 'inbound.sections.customer_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.customer_message',
        controlName: 'buyMsg',
        type: 'text' as const,
        section: 'inbound.sections.customer_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },

      // Returner Information Section (for Return Orders)
      {
        label: 'inbound.fields.returner_name',
        controlName: 'returnerName',
        type: 'text' as const,
        section: 'inbound.sections.returner_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.returner_phone',
        controlName: 'returnerPhone',
        type: 'text' as const,
        section: 'inbound.sections.returner_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
      {
        label: 'inbound.fields.returner_address',
        controlName: 'returnerAddress',
        type: 'text' as const,
        section: 'inbound.sections.returner_information',
        readonly: true,
        hidden: false, // Will be updated dynamically
      },
    ];
  }

  private initializeTableSections(): void {
    this.updateTableColumns();
    this.updateTableSections();
  }

  private updateTableColumns(): void {
    // Base columns that are always shown
    const baseColumns: TableColumn[] = [
      {
        label: 'inbound.fields.sku_code',
        prop: 'skuCode',
        type: 'text' as const,
        width: 150,
        align: 'left',
      },
      {
        label: 'inbound.fields.sku_name',
        prop: 'skuName',
        type: 'text' as const,
        width: 200,
        align: 'left',
      },
      {
        label: 'inbound.fields.spu_code',
        prop: 'spuCode',
        type: 'text' as const,
        width: 150,
        align: 'left',
      },
      {
        label: 'inbound.fields.spu_name',
        prop: 'spuName',
        type: 'text' as const,
        width: 200,
        align: 'left',
      },
      {
        label: 'inbound.fields.lot',
        prop: 'lotValue',
        type: 'text' as const,
        width: 150,
        align: 'left',
      },
      {
        label: 'inbound.fields.serial',
        prop: 'serialNumbers',
        type: 'text' as const,
        width: 150,
        align: 'left',
      },
      {
        label: 'inbound.fields.planned_quantity',
        prop: 'plannedQty',
        type: 'text' as const,
        width: 120,
        align: 'right',
      },
      {
        label:
          this.tabType === 'inbound-result'
            ? 'inbound.fields.received_quantity'
            : 'inbound.fields.already_received_quantity',
        prop: 'alreadyQty',
        type: 'text' as const,
        width: 120,
        align: 'right',
      },
    ];

    // Note: Removed received quantity column for result view as already received quantity is sufficient

    this.itemTableColumns = baseColumns;
  }

  private updateTableSections(): void {
    // Format the data to include formatted numbers
    const formattedData = (this.itemDetails || []).map((item) => {
      const formattedItem: any = {
        ...item,
        // Use individual item's planned quantity
        plannedQty: this.formatNumber(item.plannedQty),
        // Format already received quantity
        alreadyQty: this.formatNumber(item.alreadyQty),
      };

      // Note: Removed receivedQty formatting as the column is no longer displayed in result view

      return formattedItem;
    });

    this.tableSections = [
      {
        title: 'inbound.sections.item_details',
        show: true,
        columns: this.itemTableColumns,
        data: formattedData,
        loading: false, // Set to false to prevent infinite loading
        hasCheckbox: false,
        totalRecords: (this.itemDetails || []).length,
        first: 0,
        rows: 20,
      },
    ];
  }

  private updateFormFields(): void {
    this.initializeFormFields();
    this.updateFieldVisibility();
  }

  private initializeStatusForm(): void {
    this.statusForm = this.fb.group({
      status: ['', Validators.required],
    });
  }

  private updateStatusForm(): void {
    if (this.inboundData && this.statusForm) {
      this.selectedStatus = this.inboundData.status || '';
      this.statusForm.patchValue({
        status: this.selectedStatus,
      });

      // Update status options based on statusPossibleNext
      this.updateStatusOptions((this.inboundData as any).statusPossibleNext);

      // Load available status actions for dynamic buttons
      this.loadAvailableStatusActions(
        (this.inboundData as any).statusPossibleNext
      );
    }
  }

  private updateStatusOptions(statusPossibleNext?: string[]): void {
    if (statusPossibleNext && statusPossibleNext.length > 0) {
      // Create options from statusPossibleNext with translated labels
      this.statusOptions = statusPossibleNext.map((status) => {
        return {
          label: this.getInboundStatusLabel(status),
          value: status,
        };
      });
    } else if (statusPossibleNext && statusPossibleNext.length === 0) {
      // If statusPossibleNext is empty array, only show current status
      const currentStatus = this.inboundData?.status;
      if (currentStatus) {
        this.statusOptions = [
          {
            label: this.getInboundStatusLabel(currentStatus),
            value: currentStatus,
          },
        ];
      } else {
        this.statusOptions = [];
      }
    } else {
      // Fallback to all available status options if statusPossibleNext is not provided
      this.statusOptions = INBOUND_NOTICE_STATUS_OPTIONS.map((option) => ({
        label: this.getInboundStatusLabel(option.value),
        value: option.value,
      }));
    }
  }

  onStatusChange(): void {
    this.selectedStatus = this.statusForm.get('status')?.value;
    this.statusChange.emit({ status: this.selectedStatus });
  }

  private loadAvailableStatusActions(statusPossibleNext?: string[]): void {
    if (statusPossibleNext && statusPossibleNext.length > 0) {
      // Create status action buttons from statusPossibleNext
      this.availableStatusActions = statusPossibleNext.map((status) => {
        return {
          code: status,
          displayName: this.getInboundStatusLabel(status),
          label: this.getInboundStatusLabel(status),
          action: 'status_action',
          severity: 'primary',
        };
      });
    } else {
      this.availableStatusActions = [];
    }
  }

  onGoBack(): void {
    // Navigate back to the previous page
    this.formAction.emit({ action: 'go_back' });
  }

  onStatusAction(statusCode: string): void {
    // Emit status change with button_click action to distinguish from dropdown selection
    this.statusChange.emit({ status: statusCode, action: 'button_click' });
  }

  onReceiveAction(): void {
    // Emit receive action to parent component
    this.statusChange.emit({ status: 'RECEIVE', action: 'receive_click' });
  }

  private updateFieldVisibility(): void {
    if (!this.formFields || !this.inboundData) return;

    const inboundType = this.inboundData.inputType || '';

    // Apply dynamic field visibility based on inbound type
    this.formFields.forEach((field) => {
      field.hidden = this.shouldHideField(field, inboundType);
    });
  }

  private shouldHideField(field: FormField, inboundType: string): boolean {
    const section = field.section;

    // Always show basic information and warehouse information
    if (
      section === 'inbound.sections.basic_information' ||
      section === 'inbound.sections.warehouse_information'
    ) {
      return false;
    }

    // Apply logic based on inbound type
    switch (inboundType) {
      case InboundType.PURCHASE_ORDER:
        // Show: Customer Information, Warehouse Information
        // Hide: Returner Information, Store Information
        return (
          section === 'inbound.sections.returner_information' ||
          section === 'inbound.sections.store_information'
        );

      case InboundType.TRANSFER_ORDER:
        // Show: Warehouse Information, Store Information
        // Hide: Returner Information, Customer Information
        return (
          section === 'inbound.sections.returner_information' ||
          section === 'inbound.sections.customer_information'
        );

      case InboundType.RETURN_ORDER:
        // Show: Returner Information, Warehouse Information
        // Hide: Customer Information, Store Information
        return (
          section === 'inbound.sections.customer_information' ||
          section === 'inbound.sections.store_information'
        );

      default:
        // Default: show all fields
        return false;
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'go_back') {
      this.onGoBack();
    } else if (event.action === 'status_action' && event.data?.statusCode) {
      this.onStatusAction(event.data.statusCode);
    } else if (event.action === 'receive_action') {
      // Handle the new Receive action
      this.onReceiveAction();
    } else {
      // Emit other actions (like legacy cancel/save) to parent
      this.formAction.emit(event);
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    this.tableAction.emit(event);
  }

  getFormData(): any {
    if (!this.inboundData) return null;

    // Extract logistics data from logisticsList
    const logisticsList = (this.inboundData as any).logisticsList;
    let logisticsCompanyName = '';
    let logisticsNumber = '';

    if (
      logisticsList &&
      Array.isArray(logisticsList) &&
      logisticsList.length > 0
    ) {
      const firstLogistics = logisticsList[0];
      logisticsCompanyName = firstLogistics.logisticsCompanyName || '';
      logisticsNumber = firstLogistics.logisticsNo || '';
    }

    // Extract return information for return orders
    const returnerName = (this.inboundData as any).returnsName || '';
    const returnerPhone = (this.inboundData as any).returnsTel || '';
    const returnerAddress = (this.inboundData as any).returnsAddress || '';

    // Clean the inbound data to replace undefined/null values with empty strings
    const cleanedData = this.cleanUndefinedValues(this.inboundData);

    return {
      ...cleanedData,
      // Add logistics data
      logisticsCompanyName,
      logisticsNumber,
      // Add return information data
      returnerName,
      returnerPhone,
      returnerAddress,
    };
  }

  private cleanUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) return '';
    if (typeof obj !== 'object') {
      // Handle primitive values
      if (
        obj === 'undefined' ||
        obj === 'null' ||
        obj === 'Null' ||
        obj === 'NULL' ||
        obj === 'Undefined' ||
        obj === 'UNDEFINED' ||
        String(obj).trim() === '' ||
        String(obj).trim() === 'undefined' ||
        String(obj).trim() === 'null'
      ) {
        return ' ';
      }
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.cleanUndefinedValues(item));
    }
    const cleaned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const cleanedValue = this.cleanUndefinedValues(obj[key]);
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }

  getInboundTypeLabel(inputType: string): string {
    return getInboundTypeLabel(inputType, this.translateService);
  }

  getInboundStatusLabel(status: string): string {
    return getInboundStatusLabel(status, this.translateService);
  }

  getStatusSeverity(status: string): string {
    return getInboundStatusSeverity(status);
  }

  getStatusStyle(status: string): any {
    return getInboundStatusStyle(status);
  }

  formatCreationTime(dateString: string): string {
    if (!dateString) return '-';

    try {
      return this.formatDateTimeUserFriendly(dateString);
    } catch {
      return '-';
    }
  }

  getHeaderActions(): any[] {
    const actions: any[] = [];

    // Default buttons (always visible)
    actions.push({
      label: 'common.actions.go_back',
      action: 'go_back',
      outlined: true,
      severity: 'secondary',
    });

    // Flexible status action buttons (only for edit mode)
    if (this.editMode && this.availableStatusActions.length > 0) {
      // First, add Cancel Warrant button if CANCELLED status is available
      const cancelledAction = this.availableStatusActions.find(
        (statusAction) => statusAction.code === 'CANCELLED'
      );
      if (cancelledAction) {
        actions.push({
          label: 'inbound.actions.cancel_warrant',
          action: 'status_action',
          data: { statusCode: cancelledAction.code },
          outlined: true,
          severity: 'danger',
        });
      }

      // Check if PARTIALLY_RECEIVED or RECEIVED statuses are available
      const hasReceiveStatuses = this.availableStatusActions.some(
        (statusAction) =>
          statusAction.code === 'PARTIALLY_RECEIVED' ||
          statusAction.code === 'RECEIVED'
      );

      if (hasReceiveStatuses) {
        // Show single "Receive" button for both PARTIALLY_RECEIVED and RECEIVED
        actions.push({
          label: 'inbound.actions.receive',
          action: 'receive_action',
          data: { statusCode: 'RECEIVE' },
          severity: 'info',
        });
      }

      // Handle other status actions (excluding CANCELLED, PARTIALLY_RECEIVED, and RECEIVED)
      this.availableStatusActions.forEach((statusAction) => {
        // Skip CANCELLED (already added), PARTIALLY_RECEIVED and RECEIVED (handled by Receive button)
        if (
          statusAction.code === 'CANCELLED' ||
          statusAction.code === 'PARTIALLY_RECEIVED' ||
          statusAction.code === 'RECEIVED'
        ) {
          return;
        }

        // Regular status buttons - use action_status for present tense verbs
        actions.push({
          label: `inbound.action_status.${statusAction.code.toLowerCase()}`,
          action: 'status_action',
          data: { statusCode: statusAction.code },
          severity: 'info',
        });
      });
    }

    return actions;
  }

  isLogisticsSectionEmpty(): boolean {
    if (!this.inboundData) return true;

    // Check if logisticsList exists and has data
    const logisticsList = (this.inboundData as any).logisticsList;
    if (
      !logisticsList ||
      !Array.isArray(logisticsList) ||
      logisticsList.length === 0
    ) {
      return true;
    }

    // Check if any logistics item has company name or number
    return logisticsList.every(
      (logistics: any) =>
        (!logistics.logisticsCompanyName ||
          logistics.logisticsCompanyName === '') &&
        (!logistics.logisticsNo || logistics.logisticsNo === '')
    );
  }

  isWarehouseSectionEmpty(): boolean {
    if (!this.inboundData) return true;

    const warehouseFields = [
      this.inboundData.receivingLogicWarehouseCode,
      this.inboundData.receivingLogicWarehouseName,
    ];

    return warehouseFields.every(
      (field) => !field || field === '' || field === 'undefined'
    );
  }

  isSalesSectionEmpty(): boolean {
    if (!this.inboundData) return true;

    const salesFields = [
      this.inboundData.storeName,
      this.inboundData.channelTypeCode,
    ];

    return salesFields.every(
      (field) => !field || field === '' || field === 'undefined'
    );
  }

  getFieldsBySection(section: string): any[] {
    return this.formFields.filter((field) => field.section === section);
  }

  private forceRerender(): void {
    this.cdr.detectChanges();
  }

  private isQuantityField(controlName: string): boolean {
    const quantityFields = [
      'alreadyTotalQty', // Required Inbound Quantity
      'totalQty', // Total Quantity
      'plannedQty', // Planned Quantity in table
    ];
    return quantityFields.includes(controlName);
  }

  private formatNumber(value: any): string {
    // Handle null, undefined, empty string, or invalid values
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'undefined' ||
      value === 'null'
    ) {
      return ' ';
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      return ' ';
    }

    // Format with thousand separators (including 0)
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  getFieldValue(field: any): string {
    if (!this.inboundData) return ' ';

    const formData = this.getFormData();
    const value = formData[field.controlName];

    // Direct check for undefined/null/empty values
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value === 'undefined' ||
      value === 'null' ||
      value === 'Null' ||
      value === 'NULL' ||
      value === 'Undefined' ||
      value === 'UNDEFINED' ||
      String(value).trim() === '' ||
      String(value).trim() === 'undefined' ||
      String(value).trim() === 'null'
    ) {
      return ' ';
    }

    // Format number fields
    if (this.isQuantityField(field.controlName)) {
      return this.formatNumber(value);
    }

    // Translate status field values
    if (field.controlName === 'status') {
      return this.getInboundStatusLabel(value);
    }

    // Translate inbound type field values
    if (field.controlName === 'inputType') {
      return this.getInboundTypeLabel(value);
    }

    // Format date fields
    if (field.controlName === 'creationDate') {
      return this.formatDateTimeUserFriendly(value);
    }

    return value;
  }

  getPageTitle(): string {
    if (this.addMode) {
      return this.tabType === 'inbound-notice'
        ? this.translateService.instant('inbound.pages.add_inbound_notice')
        : this.translateService.instant('inbound.pages.add_inbound_warrant');
    } else if (this.editMode) {
      return this.tabType === 'inbound-notice'
        ? this.translateService.instant('inbound.pages.edit_inbound_notice')
        : this.translateService.instant('inbound.pages.edit_inbound_warrant');
    } else {
      if (this.tabType === 'inbound-notice') {
        return this.translateService.instant(
          'inbound.pages.inbound_notice_details'
        );
      } else if (this.tabType === 'inbound-result') {
        return this.translateService.instant(
          'inbound.pages.inbound_result_details'
        );
      } else {
        return this.translateService.instant(
          'inbound.pages.inbound_warrant_details'
        );
      }
    }
  }

  // Helper methods for dynamic section visibility based on inbound type
  isPurchaseOrder(): boolean {
    return this.inboundData?.inputType === InboundType.PURCHASE_ORDER;
  }

  isTransferOrder(): boolean {
    return this.inboundData?.inputType === InboundType.TRANSFER_ORDER;
  }

  isReturnOrder(): boolean {
    return this.inboundData?.inputType === InboundType.RETURN_ORDER;
  }

  // Show customer information section for purchase orders
  // Temporarily hidden - only show for return orders
  showCustomerInformation(): boolean {
    return false; // Temporarily disabled
  }

  // Show warehouse information - always visible
  showWarehouseInformation(): boolean {
    return true;
  }

  // Show store information for transfer orders
  showStoreInformation(): boolean {
    return this.isTransferOrder();
  }

  // Show returner information for return orders
  showReturnerInformation(): boolean {
    return this.isReturnOrder();
  }

  // Get grid layout class based on number of fields in section
  getGridLayoutClass(sectionKey: string): string {
    const fields = this.getFieldsBySection(sectionKey).filter(
      (field) => !field.hidden
    );
    const fieldCount = fields.length;

    if (fieldCount > 2) {
      return 'grid gap-2 grid-cols-5';
    } else {
      return 'grid gap-2 grid-cols-2'; // Each section has its own 2-column grid
    }
  }

  // Get field width class based on number of fields in section
  getFieldWidthClass(sectionKey: string): string {
    const fields = this.getFieldsBySection(sectionKey).filter(
      (field) => !field.hidden
    );
    const fieldCount = fields.length;

    if (fieldCount > 2) {
      return 'col-span-1'; // Each field takes 1 column in 5-column grid
    } else if (fieldCount === 2) {
      return 'col-span-1'; // Each field takes 1 column in 2-column grid (side-by-side)
    } else {
      return 'col-span-2'; // Single field takes full width of 2-column grid
    }
  }

  // Check if section should be in a 2-column outer grid (for 1-2 fields)
  shouldUseOuterGrid(sectionKey: string): boolean {
    const fields = this.getFieldsBySection(sectionKey).filter(
      (field) => !field.hidden
    );
    return fields.length <= 2;
  }

  // Get outer grid class for sections with 1-2 fields
  getOuterGridClass(): string {
    return 'grid gap-6 grid-cols-2';
  }

  // Format date and time in user-friendly format
  formatDateTimeUserFriendly(value: any): string {
    return formatDateTimeUserFriendly(
      value,
      this.translateService.getCurrentLang()
    );
  }

}
